import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Warning, ClockClockwise, FileText, CreditCard, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

const SEV_COLORS = {
  expired: "bg-red-500",
  urgent_7d: "bg-orange-500",
  urgent: "bg-orange-500",
  warning_30d: "bg-amber",
  warning: "bg-amber",
};

const KIND_ICONS = {
  part: ClockClockwise,
  maintenance: ClockClockwise,
  ad: Warning,
  missing_doc: FileText,
  billing_past_due: CreditCard,
  trial_ending: CreditCard,
};

const POLL_INTERVAL_MS = 60_000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications/summary");
      setData(r.data);
    } catch {
      // 401 etc. — just ignore, sidebar still mounts
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const h = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(h);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const totalUnread = data?.total_unread || 0;
  const hasUrgent = (data?.counts?.overdue || 0) > 0;

  const goTo = (item) => {
    setOpen(false);
    if (item.kind === "billing_past_due" || item.kind === "trial_ending") {
      navigate("/billing");
      return;
    }
    if (item.kind === "missing_doc") {
      navigate("/documents");
      return;
    }
    if (item.aircraft_id) {
      navigate(`/fleet/${item.aircraft_id}`);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-9 h-9 border border-zinc-800 hover:border-amber text-zinc-300 hover:text-amber transition-colors"
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={16} />
        {totalUnread > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 ${hasUrgent ? "bg-red-500" : "bg-amber"} text-black text-[10px] font-mono font-semibold rounded-full flex items-center justify-center`}
            data-testid="notification-badge"
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed lg:absolute top-14 lg:top-full mt-0 lg:mt-2 right-2 lg:right-auto lg:left-0 w-[min(420px,calc(100vw-1rem))] z-50 bg-[#0F0F0F] border border-zinc-800 shadow-2xl"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">Notifications</div>
              <div className="text-sm font-medium">{totalUnread} unread</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-amber p-1" aria-label="Close" data-testid="notification-close">
              <X size={16} />
            </button>
          </div>

          {/* Subscription alert always at top */}
          {data?.subscription_alert && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/billing"); }}
              className="w-full flex items-start gap-3 px-4 py-3 border-b border-zinc-900 bg-amber/5 hover:bg-amber/10 text-left transition-colors"
              data-testid="notification-billing-alert"
            >
              <CreditCard size={18} className="text-amber mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono uppercase tracking-wider text-amber mb-0.5">{data.subscription_alert.kind === "billing_past_due" ? "Payment failed" : "Trial ending"}</div>
                <div className="text-sm text-zinc-200 leading-snug">{data.subscription_alert.message}</div>
              </div>
              <ArrowRight size={14} className="text-zinc-500 mt-0.5" />
            </button>
          )}

          {/* Quick stat row */}
          {data?.counts && (
            <div className="grid grid-cols-3 gap-px bg-zinc-900 border-b border-zinc-900">
              <Stat label="Overdue" value={data.counts.overdue} accent={data.counts.overdue ? "red" : "zinc"} />
              <Stat label="Urgent" value={data.counts.urgent} accent={data.counts.urgent ? "orange" : "zinc"} />
              <Stat label="Missing docs" value={data.counts.missing_docs} accent={data.counts.missing_docs ? "amber" : "zinc"} />
            </div>
          )}

          {/* Item list */}
          <div className="max-h-[420px] overflow-y-auto" data-testid="notification-list">
            {loading && !data && (
              <div className="p-6 text-center text-xs text-zinc-500 font-mono">Loading…</div>
            )}
            {data && data.items.length === 0 && !data.subscription_alert && (
              <div className="p-8 text-center" data-testid="notification-empty">
                <div className="text-zinc-300 text-sm">All clear ✓</div>
                <div className="text-xs text-zinc-500 mt-1">No urgent items, missing documents, or billing issues.</div>
              </div>
            )}
            {data?.items.map((item, i) => {
              const Icon = KIND_ICONS[item.kind] || Warning;
              const color = SEV_COLORS[item.severity] || "bg-zinc-600";
              return (
                <button
                  key={`${item.kind}-${item.id || i}`}
                  type="button"
                  onClick={() => goTo(item)}
                  className="w-full flex items-start gap-3 px-4 py-3 border-b border-zinc-900 hover:bg-zinc-950 text-left transition-colors"
                  data-testid={`notification-item-${i}`}
                >
                  <span className={`${color} w-1.5 h-1.5 mt-2 rounded-full shrink-0`} />
                  <Icon size={16} className="text-zinc-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{item.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 font-mono">
                      {item.aircraft_registration && <span className="text-amber">{item.aircraft_registration}</span>}
                      {item.due_label && <span>· {item.due_label}</span>}
                    </div>
                  </div>
                  <ArrowRight size={12} className="text-zinc-600 mt-1" />
                </button>
              );
            })}
          </div>

          <div className="px-4 py-2 border-t border-zinc-800 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            <button onClick={load} className="hover:text-amber" data-testid="notification-refresh">Refresh</button>
            <button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="hover:text-amber" data-testid="notification-go-dashboard">Open dashboard →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colorCls = {
    red: "text-red-400",
    orange: "text-orange-400",
    amber: "text-amber",
    zinc: "text-zinc-500",
  }[accent] || "text-zinc-300";
  return (
    <div className="bg-[#0F0F0F] py-2.5 px-2 text-center">
      <div className={`text-lg font-semibold tabular-nums ${colorCls}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</div>
    </div>
  );
}
